/**
 * Il profilo dei flussi: **dove si perde il sistema** (F-137).
 *
 * La pagina degli esiti chiede «il sistema aveva ragione?». Questa sezione
 * chiede la domanda gemella, e ne è il complemento: «dove si perde?». Sono due
 * facce dello stesso periodo, e per questo stanno nella stessa pagina invece
 * che in due — condividono il filtro, la disciplina dei «non misurati» e la
 * sorgente dei costi.
 *
 * ⚠️ **La riga più importante è la prima dell'imbuto**, ed è quella che prima
 * non esisteva: su venti titoli valutati dal digest ne diventavano quattro
 * candidati, e i sedici morti sparivano con le loro otto ragioni. Ogni incidente
 * serio di settembre è stato diagnosticato leggendo un log a mano quando sarebbe
 * bastato che qualcuno contasse.
 */

import { formatDate, formatNumber } from '@/lib/format';
import { Alert, Card, EmptyState, Spinner, StatCard, TableWrap } from '@/ui';
import { useFlowProfile, type FlowStage } from './api';
import './outcomes.css';

/** I codici così come li scrive il motore, tradotti una volta sola. */
const REASON_LABELS: Record<string, string> = {
  hold_rating: 'Giudizio «Hold»: nessuna direzione',
  no_instrument: 'Titolo non a catalogo',
  new_position_without_wishlist: 'Titolo nuovo senza wishlist',
  amount_not_computable: 'Importo non calcolabile',
  below_min_amount: 'Sotto l’importo minimo',
  no_position_to_sell: 'Nessuna posizione da vendere',
  quantity_not_positive: 'Quantità non positiva',
  capped_by_max_proposals: 'Tagliato dal tetto di proposte per run',
  // Revisione dei profitti (F-138): vendite chieste dal modello e fermate
  // dal post-filtro della routine, prima del motore.
  position_unknown: 'Titolo non in posizione (revisione dei profitti)',
  position_not_sellable: 'Posizione non vendibile: in perdita o senza P&L noto',
  sell_step_invalid: 'Taglio non ammesso (revisione dei profitti)',
  duplicate_symbol: 'Stesso titolo proposto due volte (revisione dei profitti)',
  pending_proposal_exists: 'Una proposta gemella era già in attesa in un altro run',
  superseded_by_execution: 'Una proposta gemella era già stata eseguita oggi',
  llm_reject: 'Il modello ha detto no',
  llm_check_failed: 'Verifica non riuscita (guasto)',
  llm_budget_exceeded: 'Budget di verifica del run esaurito',
  stale_data: 'Dati non abbastanza freschi',
  no_price: 'Nessun prezzo di riferimento',
  no_broker_ticker: 'Manca il ticker del broker',
  no_exchange_pair: "Coppia non a catalogo sull'exchange",
  swap_unsupported: 'Scambio non eseguibile',
  crypto_rank_out_of_range: 'Crypto fuori dalle prime 200',
  no_fx_rate: 'Cambio non disponibile',
  cash_reserve: 'Riserva di liquidità',
  limit_exceeded: 'Limiti operativi superati',
  wishlist_expired: 'Wishlist scaduta',
  wishlist_exceeded: 'Oltre il vincolo di wishlist',
  portfolio_off: 'Portafoglio spento',
  failed: 'Fallito',
  pending: 'In attesa',
  running: 'In corso',
};

function labelOf(reason: string): string {
  return REASON_LABELS[reason] ?? reason;
}

/**
 * Una barra proporzionale allo stadio più largo.
 *
 * ⚠️ La scala è **comune a tutti gli stadi**: normalizzare ciascuno sul proprio
 * massimo darebbe sei barre piene e nessun imbuto — cioè il disegno che
 * nasconde esattamente ciò che si sta guardando.
 */
function StageBar({ stage, widest }: { stage: FlowStage; widest: number }) {
  const quota = widest === 0 ? 0 : (stage.entered / widest) * 100;
  const superstiti = stage.entered === 0 ? 0 : (stage.passed / stage.entered) * 100;
  return (
    <div className="funnel__bar" style={{ width: `${Math.max(quota, 2)}%` }}>
      <div className="funnel__bar-passed" style={{ width: `${superstiti}%` }} />
    </div>
  );
}

export function FlowProfileSection({ days }: { days: number }) {
  const { data, isPending, error } = useFlowProfile(days);

  if (isPending) return <Spinner label="Calcolo il profilo dei flussi…" />;
  if (error) {
    return (
      <Alert variant="error" title="Profilo non caricato">
        {error instanceof Error ? error.message : 'Errore imprevisto.'}
      </Alert>
    );
  }

  // ⚠️ Letture difensive: questa sezione e' **diagnostica**, e una risposta di
  // forma inattesa non deve poter cancellare la pagina che la ospita. E' lo
  // stesso ragionamento della scheda degli esiti sul titolo — un riquadro che
  // si rompe non deve portarsi via cio' che gli sta accanto.
  const stages = data?.stages ?? [];
  const wasteRows = data?.waste?.rows ?? [];
  const recalcRows = data?.recalc?.minOperationAmount ?? [];
  const problems = data?.problems ?? [];
  const widest = Math.max(...stages.map((stage) => stage.entered), 0);
  const nothing = stages.length === 0 || stages.every((stage) => stage.entered === 0);

  return (
    <>
      {/* ⚠️ Un imbuto che non quadra si MOSTRA con il suo difetto: chi guarda
          deve sapere che quei numeri non tornano, e perché. Nasconderlo dietro
          un errore lascerebbe leggere numeri sbagliati come se fossero giusti. */}
      {problems.length > 0 && (
        <Alert variant="warning" title="⚠️ L’imbuto non quadra" items={problems}>
          Entrati, usciti e morti non tornano su uno stadio. I numeri qui sotto vanno letti sapendolo:
          è un difetto del profilo, non del sistema misurato.
        </Alert>
      )}

      {nothing ? (
        <EmptyState
          icon="🫙"
          title="Nessun flusso nel periodo"
          description={`Fra il ${formatDate(data.from)} e il ${formatDate(data.to)} non risultano digest, analisi o proposte. Prova ad allargare il periodo.`}
        />
      ) : (
        <>
          <div className="outcomes__stats">
            <StatCard label="Titoli valutati dal digest" value={formatNumber(data.briefingItems)} />
            <StatCard
              label="Analisi di dettaglio"
              value={formatNumber(data.analyses.completed)}
              hint={
                data.analyses.failed > 0
                  ? `${formatNumber(data.analyses.failed)} fallite su ${formatNumber(data.analyses.requested)} richieste`
                  : `su ${formatNumber(data.analyses.requested)} richieste`
              }
              tone={data.analyses.failed > 0 ? 'negative' : 'neutral'}
            />
            <StatCard
              label="Durata mediana di un’analisi"
              value={data.analyses.medianMinutes === null ? '—' : `${data.analyses.medianMinutes} min`}
              hint="È lo stadio più caro, e l’unico di cui i tempi siano già misurati"
            />
            <StatCard
              label="Giudizi misurabili"
              value={formatNumber(data.measurableOutcomes)}
              hint="Non è l’ultimo stadio dell’imbuto: conta giudizi, non ordini"
            />
          </div>

          <Card
            title="Dove si perde il sistema"
            subtitle={`Dal ${formatDate(data.from)} al ${formatDate(data.to)}. Ogni stadio: quanti sono entrati, quanti passati, e per quale motivo gli altri no.`}
            flush
          >
            <TableWrap>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Stadio</th>
                    <th className="col--secondary">Imbuto</th>
                    <th className="numeric">Entrati</th>
                    <th className="numeric">Passati</th>
                    <th className="numeric">Mortalità</th>
                    <th>Perché gli altri no</th>
                  </tr>
                </thead>
                <tbody>
                  {stages.map((stage) => (
                    <tr key={stage.key}>
                      <td>{stage.label}</td>
                      <td className="col--secondary">
                        <StageBar stage={stage} widest={widest} />
                      </td>
                      <td className="numeric">{formatNumber(stage.entered)}</td>
                      <td className="numeric">{formatNumber(stage.passed)}</td>
                      <td className="numeric">
                        {/* ⚠️ `null` non è zero: è «non c’è niente da misurare». */}
                        {stage.mortalityPct === null ? (
                          <span className="cell--minor">—</span>
                        ) : (
                          `${stage.mortalityPct}%`
                        )}
                      </td>
                      <td>
                        {stage.deaths.length === 0 ? (
                          <span className="cell--minor">—</span>
                        ) : (
                          <ul className="funnel__deaths">
                            {stage.deaths.map((death) => (
                              <li key={death.reason}>
                                {labelOf(death.reason)} <strong>{formatNumber(death.count)}</strong>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>

          <Card
            title="Spesa che non ha prodotto niente"
            subtitle="⚠️ «Senza conseguenza» non significa «sprecata»: il numero dice quanto, non se sia giusto."
            flush
          >
            <TableWrap>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Voce</th>
                    <th className="numeric">Casi</th>
                    <th className="numeric">Costo</th>
                    <th>Come leggerla</th>
                  </tr>
                </thead>
                <tbody>
                  {wasteRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="numeric">{formatNumber(row.count)}</td>
                      <td className="numeric">
                        {row.cost === null ? (
                          <span className="cell--minor">—</span>
                        ) : (
                          `$ ${Number(row.cost).toFixed(4)}`
                        )}
                      </td>
                      <td className="cell--minor">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>

          <Card
            title="Se la soglia fosse un’altra"
            subtitle="Ricalcolo esatto a UN passo sugli importi del periodo — non una simulazione."
          >
            {/* ⚠️ La differenza va scritta, non lasciata intuire: dopo il primo
                ordine cambiato tutto il seguito è un mondo diverso, e la
                verifica del modello non si rigioca. Un «avresti guadagnato il
                12% in più» sarebbe una bugia costruita bene. */}
            <TableWrap>
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>Importo minimo</th>
                    <th className="numeric">Non sarebbero nate</th>
                    <th className="numeric">Sarebbero nate</th>
                    <th className="numeric col--secondary">Importo ignoto</th>
                  </tr>
                </thead>
                <tbody>
                  {recalcRows.map((row) => (
                    <tr key={row.threshold}>
                      <td>{row.threshold} €</td>
                      <td className="numeric">{formatNumber(row.wouldDie)}</td>
                      <td className="numeric">{formatNumber(row.wouldSurvive)}</td>
                      <td className="numeric col--secondary">
                        {row.unknown > 0 ? formatNumber(row.unknown) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
            <p className="outcomes__disclaimer">
              È un ricalcolo <strong>a un passo</strong>: conta quali proposte del periodo non sarebbero
              nate con una soglia diversa. Non rigioca la catena — dopo il primo ordine cambiato
              posizioni, liquidità e tetto sono un altro mondo, e la verifica del modello non si
              ripete. {data.recalc.cappedByMaxProposals > 0 && (
                <>
                  Nel periodo <strong>{formatNumber(data.recalc.cappedByMaxProposals)}</strong> candidati
                  validi sono stati tagliati dal tetto di proposte per run.
                </>
              )}
            </p>
          </Card>
        </>
      )}
    </>
  );
}
